import { Investor } from '@/types/investor';

export interface ActivityItem {
  id: string;
  type: 'created' | 'updated' | 'status_change' | 'document_upload' | 'approved' | 'rejected';
  investorId: string;
  investorName: string;
  action: string;
  timestamp: string;
  details?: string;
}

export const generateRecentActivity = (investors: Investor[]): ActivityItem[] => {
  const activities: ActivityItem[] = [];

  investors.forEach(investor => {
    // Add creation activity
    if (investor.createdAt) {
      activities.push({
        id: `${investor.id}-created`,
        type: 'created',
        investorId: investor.id,
        investorName: investor.investor,
        action: 'Investor created',
        timestamp: investor.createdAt
      });
    }

    // Add history items as activities
    if (investor.history && investor.history.length > 0) {
      investor.history.forEach((historyItem, index) => {
        let activityType: ActivityItem['type'] = 'updated';

        // Determine activity type based on history action
        if (historyItem.action.toLowerCase().includes('approved')) {
          activityType = 'approved';
        } else if (historyItem.action.toLowerCase().includes('rejected')) {
          activityType = 'rejected';
        } else if (historyItem.action.toLowerCase().includes('status')) {
          activityType = 'status_change';
        } else if (historyItem.action.toLowerCase().includes('document')) {
          activityType = 'document_upload';
        }

        activities.push({
          id: `${investor.id}-history-${index}`,
          type: activityType,
          investorId: investor.id,
          investorName: investor.investor,
          action: historyItem.action,
          timestamp: historyItem.timestamp,
          details: historyItem.details
        });
      });
    }

    // Add wholesale approval activities
    if (investor.status === 'Approved' && investor.wholesaleApprovedDate) {
      activities.push({
        id: `${investor.id}-wholesale-approved`,
        type: 'approved',
        investorId: investor.id,
        investorName: investor.investor,
        action: 'Wholesale approved',
        timestamp: investor.wholesaleApprovedDate,
        details: `Approved by ${investor.wholesaleAeName || investor.aeName}`
      });
    }

    // Add correspondent approval activities
    if (investor.correspondentStatus === 'Approved' && investor.correspondentApprovedAsOfDate) {
      activities.push({
        id: `${investor.id}-correspondent-approved`,
        type: 'approved',
        investorId: investor.id,
        investorName: investor.investor,
        action: 'Correspondent approved',
        timestamp: investor.correspondentApprovedAsOfDate,
        details: `Approved by ${investor.correspondentAeName || investor.aeName}`
      });
    }

    // Add recent document uploads (if documents exist)
    if (investor.documents && investor.documents.length > 0) {
      investor.documents
        .filter(doc => doc.isActive)
        .forEach(doc => {
          activities.push({
            id: `${investor.id}-doc-${doc.id}`,
            type: 'document_upload',
            investorId: investor.id,
            investorName: investor.investor,
            action: 'Document uploaded',
            timestamp: doc.uploadedAt,
            details: `${doc.category} (${doc.documentType})`
          });
        });
    }
  });

  // Sort by timestamp (most recent first) and limit to recent items
  return activities
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, 20); // Show last 20 activities
};

export const getActivityIcon = (type: ActivityItem['type']): string => {
  switch (type) {
    case 'created':
      return '👤';
    case 'approved':
      return '✅';
    case 'rejected':
      return '❌';
    case 'status_change':
      return '🔄';
    case 'document_upload':
      return '📄';
    case 'updated':
    default:
      return '✏️';
  }
};

export const getActivityColor = (type: ActivityItem['type']): string => {
  switch (type) {
    case 'created':
      return 'text-blue-600 dark:text-blue-400';
    case 'approved':
      return 'text-green-600 dark:text-green-400';
    case 'rejected':
      return 'text-red-600 dark:text-red-400';
    case 'status_change':
      return 'text-orange-600 dark:text-orange-400';
    case 'document_upload':
      return 'text-purple-600 dark:text-purple-400';
    case 'updated':
    default:
      return 'text-gray-600 dark:text-gray-400';
  }
};

export const formatTimeAgo = (timestamp: string): string => {
  const now = new Date();
  const activityTime = new Date(timestamp);
  const diffInMs = now.getTime() - activityTime.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInMinutes < 1) {
    return 'Just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  } else {
    return activityTime.toLocaleDateString();
  }
};